import { Clickable, OnEscapeListener } from '@carvana/merch-components';
import { useClientSide } from '@carvana/merch-react-hooks';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import React, { useCallback, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { modalZIndex } from '~/constants/common/zIndex';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  CloseIcon,
  Content,
  DesktopHeaderContainer,
  MobileHeaderContainer,
  ModalHeader,
  Wrapper
} from './Modal.styles';

const Portal = dynamic(
  () => import(/* webpackChunkName: "Portal" */ '@/common/Portal'),
  { ssr: false }
);

type OwnProps = React.PropsWithChildren<{
  className?: string;
  headerText?: string;
  id?: string;
  isLargeModal?: boolean;
  onToggle?: (event: React.MouseEvent) => void;
  open: boolean;
  shouldHideHeader?: boolean;
  whiteBg?: boolean;
  zIndex?: number;
}>;

const dummyFn = () => {};

export default function Modal({
  children,
  open,
  headerText = '',
  id = 'modal',
  isLargeModal = false,
  onToggle = dummyFn,
  whiteBg = false,
  className = '',
  shouldHideHeader = false,
  zIndex
}: OwnProps) {
  const isMobile = useIsMobile();
  const isClientSide = useClientSide();
  const wrapperRef = useRef<HTMLElement>(null);

  const Header = useCallback(
    () => (
      <>
        {headerText ? <ModalHeader>{headerText}</ModalHeader> : null}
        <Clickable
          element={CloseIcon}
          onClick={onToggle}
          data-analytics-id={null}
          data-testid="close-modal"
        />
      </>
    ),
    [headerText, onToggle]
  );

  const zIndexToUse = zIndex ?? modalZIndex;

  return isClientSide ? (
    <Portal open={open} id={id}>
      <FocusLock autoFocus={false} group="modal">
        <OnEscapeListener onEscape={onToggle}>
          <Wrapper
            id={id}
            className={shouldHideHeader ? '' : 'pt-44'}
            onClick={(e: React.MouseEvent) => {
              if (e.target === wrapperRef.current) onToggle(e);
            }}
            data-analytics-id={null}
            data-testid="modal-wrapper"
            ref={wrapperRef}
            style={{ zIndex: zIndexToUse }}
          >
            {isMobile && !shouldHideHeader && (
              <MobileHeaderContainer
                style={{ zIndex: zIndexToUse + 1 }}
                data-testid="header-container"
              >
                <Header />
              </MobileHeaderContainer>
            )}
            <Content
              className={cx(className, whiteBg ? 'bg-white' : 'bg-grey-1', {
                'l:max-w-[943px]': isLargeModal
              })}
              data-cv-test="modal-content"
              data-testid="content"
            >
              {!isMobile && !shouldHideHeader && (
                <DesktopHeaderContainer
                  style={{ zIndex: zIndexToUse + 1 }}
                  data-testid="header-container"
                >
                  <Header />
                </DesktopHeaderContainer>
              )}
              <div className="h-full overflow-auto">{children}</div>
            </Content>
          </Wrapper>
        </OnEscapeListener>
      </FocusLock>
    </Portal>
  ) : null;
}
